import React, { useState, useRef } from 'react';
import classnames from 'classnames';

import BackgroundImage from '@deliveroo/consumer-component-library/components/BackgroundImage';
import P from '@deliveroo/consumer-component-library/components/P';
import Span from '@deliveroo/consumer-component-library/components/Span';

import Bubble from '../Bubble';
import { UITextLine, UITitleLine } from '../HomeFeedUILines';

import getSafe from '../../utils/getSafe';
import useLazyLoad from '../../areas/shared/hooks/useLazyLoad';

import { UICardContentShape, UIOverlayShape, PlaceholderCardShape } from './shared/proptypes';

import greyTile from './images/grey.svg';
import styles from './styles.scss';

const classPositionMap = {
  MIDDLE: styles.overlayMiddle,
  TOP_LEADING: styles.overlayTopLeft,
  TOP_TRAILING: styles.overlayTopRight,
  BOTTOM_LEADING: styles.overlayBottomLeft,
  BOTTOM_TRAILING: styles.overlayBottomRight,
};

const buildRGBAColor = ({ r, g, b, a }) => `rgba(${r}, ${g}, ${b}, ${a})`;
const getHexColor = (data) => (data.color && data.color.hex) || 'initial';
const getRGBAColor = (color) => getSafe(() => buildRGBAColor(color)) || 'initial';

// Component to render the content for the UICard. Each UICardContent manages it's own state in order to lazy load UI.
export const UICardContent = ({
  image,
  bubble,
  uiLines,
  overlay,
  contentSize,
  preLoad,
  lazyLoadImage,
  onClick,
  url,
  isPlaceholder,
}) => {
  const expandedView = contentSize === 'expanded';
  const truncate = false; // Setting as false for now, will be controlled by the API in the future.
  const containerRef = useRef(null);

  const [isVisible, setVisibleState] = useState(Boolean(preLoad));

  /*
    If lazyLoaders are supported by the browser then subscribe to the observer and update
    the isVisible state when the component scrolls into view.
  */
  useLazyLoad({ isVisible, setVisibleState, containerRef });
  const showCard = (isVisible || preLoad) && !isPlaceholder;
  /*
    Placeholder rules:
      - If it's in a loading state and a preloaded card, render a placeholder.
      - If it's not in a loading state and the card is not preloaded or in the viewport, render a placeholder
  */
  const showPlaceholder = (isPlaceholder && preLoad) || (!isVisible && !preLoad && !isPlaceholder);
  return (
    <a
      href={url}
      className={classnames(styles.linkTag, {
        [styles.expandedView]: expandedView,
        [styles.defaultView]: !expandedView,
      })}
      key={contentSize}
      onClick={onClick}
      ref={containerRef}
    >
      {/* Only render the card content when the component is in the viewport. This prevents unnecessary calculations on large feeds. */}
      {showCard && (
        <React.Fragment>
          <span className={styles.image}>
            {/* Tell the BackgroundImage component not to use a lazyLoader for the image. We are lazy loading the parent so we don't want to also initialize a lazyloader in the BackgroundImage component.
              Small cards need the background image lazy loader to handle the transition between the default and expanded views.
              */}
            <BackgroundImage
              src={image || greyTile}
              lazyLoader={lazyLoadImage ? undefined : { isSupported: false }}
            >
              <UIOverlay overlay={overlay} />
            </BackgroundImage>
          </span>

          <span className={styles.details}>
            {bubble && (
              <div className={styles.bubble}>
                <Bubble
                  time={bubble.text}
                  label={bubble.qualifier}
                  reverseLabel={bubble.qualifierPosition === 'BEFORE'}
                  isPlaceholder={isPlaceholder}
                />
              </div>
            )}
            <div
              className={classnames(styles.cardDescription, {
                [styles.withBubble]: Boolean(bubble),
              })}
            >
              <ul>
                {uiLines &&
                  uiLines.map((uiLine) => (
                    <React.Fragment key={uiLine.key}>
                      {uiLine.typeName === 'UITitleLine' && (
                        <UITitleLine
                          text={uiLine.text}
                          color={uiLine.color}
                          isPlaceholder={isPlaceholder}
                          truncate={truncate}
                        />
                      )}
                      {uiLine.typeName === 'UITextLine' && (
                        <UITextLine uiSpans={uiLine.uiSpans} truncate={truncate} withPadding />
                      )}
                    </React.Fragment>
                  ))}
              </ul>
            </div>
          </span>
        </React.Fragment>
      )}
      {/* Render a placeholder card until the component has scrolled into view, only show the animation for content with the preloaded property to limit animations running when out of view */}
      {showPlaceholder && <PlaceholderCard isPlaceholder={isPlaceholder} withAnimation={false} />}
    </a>
  );
};

UICardContent.propTypes = UICardContentShape;

export const UIOverlay = ({ overlay }) => {
  if (!overlay) return null;
  const { backgroundColor, text, image, badge } = overlay;

  return (
    <div className={styles.overlayWrapper}>
      <div
        style={{
          backgroundColor: getRGBAColor(backgroundColor),
        }}
        className={styles.overlay}
      >
        {text && (
          <span
            style={{
              color: getHexColor(text),
            }}
            className={classnames(styles.textOverlay, classPositionMap[text.position])}
          >
            <Span size="small" isBold>
              {text.value}
            </Span>
          </span>
        )}
        {image && (
          <span className={classnames(styles.imageOverlay, classPositionMap[image.position])}>
            <BackgroundImage src={image.url} />
          </span>
        )}
        {badge && (
          <span className={classnames(styles.badgeOverlay, classPositionMap[badge.position])}>
            <ul
              className={styles.badge}
              style={{
                backgroundColor: getHexColor(badge),
              }}
            >
              <UITextLine uiSpans={badge.uiLine.uiSpans} truncate />
            </ul>
          </span>
        )}
      </div>
    </div>
  );
};

UIOverlay.propTypes = UIOverlayShape;

export const PlaceholderCard = ({ isPlaceholder, withAnimation }) => (
  <React.Fragment>
    <span className={classnames(styles.image)}>
      <div
        className={classnames(styles.placeholderImage, {
          [styles.placeholder]: isPlaceholder,
          [styles.withAnimation]: withAnimation,
        })}
      />
    </span>
    <div className={styles['placeholder-tags']}>
      <P>
        <span className={classnames(styles.placeholder, { [styles.withAnimation]: withAnimation })}>
          Placeholder
        </span>
      </P>
      <ul>
        <li>
          <span
            className={classnames(styles.placeholder, { [styles.withAnimation]: withAnimation })}
          >
            Placeholder
          </span>
          <span
            className={classnames(styles.placeholder, { [styles.withAnimation]: withAnimation })}
          >
            Placeholder
          </span>
          <span
            className={classnames(styles.placeholder, { [styles.withAnimation]: withAnimation })}
          >
            Placeholder
          </span>
          <span
            className={classnames(styles.placeholder, { [styles.withAnimation]: withAnimation })}
          >
            Placeholder
          </span>
        </li>
      </ul>
      <ul>
        <li>
          <span
            className={classnames(styles.placeholder, { [styles.withAnimation]: withAnimation })}
          >
            Placeholder
          </span>
          <span
            className={classnames(styles.placeholder, { [styles.withAnimation]: withAnimation })}
          >
            Placeholder
          </span>
        </li>
      </ul>
    </div>
  </React.Fragment>
);

PlaceholderCard.propTypes = PlaceholderCardShape;
